import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../lib/utils';
import { audioEngine } from '../utils/audioEngine';
import { PrismBuffs } from '../utils/prismSystem';
import GameModal from './GameModal';
import HowToPlay from './HowToPlay';

// FIX 1: Pre-generate spark particle offsets as a module-level constant so they
// never change between renders and Framer Motion doesn't re-create animations.
const SPARK_PARTICLES = Array.from({ length: 6 }).map((_, i) => {
  const angle = (i * 2 * Math.PI) / 6;
  const distance = 32;
  return {
    id: i,
    x: Math.cos(angle) * distance,
    y: Math.sin(angle) * distance,
    scale: 0.5,
  };
});

const TileSparks = React.memo(() => (
  <div className="absolute inset-0 pointer-events-none z-50 overflow-visible">
    {SPARK_PARTICLES.map(p => (
      <motion.div
        key={p.id}
        initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
        animate={{ x: p.x, y: p.y, scale: [0, p.scale, 0], opacity: [1, 1, 0] }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.9)]"
      />
    ))}
  </div>
));

interface TileData {
  id: string;
  value: number;
  isLocked: boolean;
  cooldownUntil: number;
  isGold?: boolean;
  isSparking?: boolean;
}

interface GoldRushGameProps {
  onGameOver: (stats: {
    score: number;
    correctAnswers: number;
    mistakes: number;
    feverAnswers: number;
    feverTriggerCount: number;
  }) => void;
  onBack: () => void;
  onUpdateMission?: (id: string, amount?: number) => void;
  prismBuffs: PrismBuffs;
  highScore: number;
  tutorialStep?: number;
  onNextTutorialStep?: () => void;
}

const ROWS = 6;
const COLS = 5;
const INITIAL_TIME = 60;

// Fisher-Yates Shuffle
const shuffle = <T,>(array: T[]): T[] => {
  const newArr = [...array];
  for (let i = newArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
  }
  return newArr;
};

// FIX 2: BullionTile wrapped in React.memo so it only re-renders when its own
// props change, not when any parent state (pointerPos, dragTotal, etc.) changes.
const BullionTile = React.memo(({ value, isSelected, isLocked, isFever }: { value: number; isSelected?: boolean; isLocked?: boolean; isFever?: boolean }) => (
  <div className={cn(
    'w-full h-full relative transition-all duration-200 rounded-lg overflow-hidden active:scale-95',
    isSelected && 'scale-110 z-30 rotate-1 shadow-2xl',
    isLocked && 'scale-110 brightness-125 z-40'
  )}>
    <div className={cn(
      'absolute inset-0 rounded-lg border-b-4 transition-all duration-200',
      isLocked ? 'bg-yellow-300 border-yellow-400 shadow-[0_0_30px_rgba(251,191,36,1)]' :
      isSelected ? 'bg-amber-400 border-amber-600 shadow-[0_0_20px_rgba(251,191,36,0.6)]' :
      isFever ? 'bg-amber-500/30 border-amber-500/50 shadow-[0_0_10px_rgba(251,191,36,0.2)]' :
      'bg-amber-500/15 border-amber-500/25'
    )} />
    <div className={cn('ingot-sheen', isLocked ? 'opacity-80' : 'opacity-30')} />
    <div className="relative z-10 flex items-center justify-center h-full">
      <span className={cn(
        'font-num font-black text-2xl transition-all duration-200',
        (isSelected || isLocked) ? 'text-slate-950 scale-110' :
        isFever ? 'text-amber-400 font-bold' : 'text-amber-500/80'
      )}>
        {value}
      </span>
    </div>
  </div>
));

const generateAscensionBuffer = () => {
  const tiles: number[] = [];
  for (let i = 0; i < 5; i++) tiles.push(5);
  [1, 2, 3, 4, 6].forEach(val => {
    tiles.push(val);
    tiles.push(val);
  });
  for (let i = 0; i < 15; i++) {
    tiles.push(Math.floor(Math.random() * 9) + 1);
  }
  return shuffle(tiles);
};

export default function GoldRushGame({ onGameOver, onBack, onUpdateMission, prismBuffs, highScore, tutorialStep, onNextTutorialStep }: GoldRushGameProps) {
  const [grid, setGrid] = useState<TileData[][]>([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(INITIAL_TIME + (prismBuffs?.extraTime || 0));
  const [currentTarget, setCurrentTarget] = useState(10);
  const [selectedPath, setSelectedPath] = useState<{ x: number; y: number }[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTotal, setDragTotal] = useState(0);
  // FIX 3: Keep pointer position in a ref — no re-renders needed just for the
  // bubble overlay. We use a separate minimal state only to trigger bubble show/hide.
  const pointerPosRef = useRef({ x: 0, y: 0 });
  const [bubblePos, setBubblePos] = useState({ x: 0, y: 0 });
  const [isFever, setIsFever] = useState(false);
  const [feverTimeLeft, setFeverTimeLeft] = useState(0);
  const [feverCounter, setFeverCounter] = useState(0);

  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [feverAnswers, setFeverAnswers] = useState(0);
  const [feverTriggerCount, setFeverTriggerCount] = useState(0);

  const isTutorialActive = tutorialStep !== undefined && tutorialStep >= 15 && tutorialStep <= 21;
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [startSequence, setStartSequence] = useState<'ready' | 'go' | null>(() => isTutorialActive ? null : 'ready');

  const gridRef = useRef<HTMLDivElement>(null);

  // FIX 4: Keep a ref copy of grid so handlePointerUp can access it without
  // needing grid in its useCallback dependency array. This prevents the
  // addEventListener/removeEventListener cycling on every tile spark.
  const gridRef2 = useRef<TileData[][]>([]);
  useEffect(() => { gridRef2.current = grid; }, [grid]);

  // FIX 5: selectedSet as a ref for O(1) lookup in render instead of
  // selectedPath.some() O(n) search on every tile every render.
  const selectedSetRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    selectedSetRef.current = new Set(selectedPath.map(p => `${p.x},${p.y}`));
  }, [selectedPath]);

  // Initialize Grid
  const initGrid = useCallback(() => {
    const buffer = generateAscensionBuffer();
    const newGrid = [];
    let idx = 0;
    for (let y = 0; y < ROWS; y++) {
      const row = [];
      for (let x = 0; x < COLS; x++) {
        let value = buffer[idx++];
        let isGold = false;

        if (tutorialStep && tutorialStep >= 15 && tutorialStep <= 19) {
          if (x === 2 && (y === 2 || y === 3)) {
            value = 5;
            if (tutorialStep <= 17) isGold = true;
          } else if (x === 3 && y === 2) {
            value = 6;
            if (tutorialStep === 18) isGold = true;
          } else if (x === 3 && y === 3) {
            value = 5;
            if (tutorialStep === 18) isGold = true;
          } else if (x === 1 && y === 2) {
            value = 6;
            if (tutorialStep === 19) isGold = true;
          } else if (x === 1 && y === 3) {
            value = 6;
            if (tutorialStep === 19) isGold = true;
          }
        } else {
          if (x === 2 && (y === 2 || y === 3)) {
            value = 5;
            isGold = true;
          }
        }

        row.push({
          id: Math.random().toString(36).substr(2, 9),
          value,
          isLocked: false,
          cooldownUntil: 0,
          isGold,
        });
      }
      newGrid.push(row);
    }
    setGrid(newGrid);
  }, [tutorialStep]);

  useEffect(() => { initGrid(); }, [initGrid]);

  // Background music
  useEffect(() => {
    if (showHowTo || startSequence !== null) return;
    audioEngine.startMusic(isFever ? 'goldrush_fever' : 'goldrush');
  }, [showHowTo, startSequence, isFever]);

  // Countdown Logic
  useEffect(() => {
    if (showHowTo) return;
    if (startSequence === 'ready') {
      audioEngine.playSound('ready');
      const t = setTimeout(() => setStartSequence('go'), 1000);
      return () => clearTimeout(t);
    } else if (startSequence === 'go') {
      audioEngine.playSound('go');
      const t = setTimeout(() => setStartSequence(null), 1000);
      return () => clearTimeout(t);
    }
  }, [startSequence, showHowTo]);

  // Main Timer
  useEffect(() => {
    if (isFever || timeLeft <= 0 || isTransitioning || showHowTo || startSequence || isTutorialActive) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => Math.max(0, prev - 0.1));
    }, 100);
    return () => clearInterval(interval);
  }, [isFever, timeLeft, isTransitioning, showHowTo, startSequence, isTutorialActive]);

  // Fever Timer
  useEffect(() => {
    if (!isFever || feverTimeLeft <= 0 || isTutorialActive) return;
    const interval = setInterval(() => {
      setFeverTimeLeft(prev => {
        if (prev <= 0.1) {
          setIsFever(false);
          setIsTransitioning(true);
          setTimeout(() => setIsTransitioning(false), 500);
          return 0;
        }
        return prev - 0.1;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [isFever, feverTimeLeft, isTutorialActive]);

  // GameOver trigger
  useEffect(() => {
    if (timeLeft === 0 && !isFever) {
      onGameOver({ score, correctAnswers, mistakes, feverAnswers, feverTriggerCount });
    }
  }, [timeLeft, isFever, score, correctAnswers, mistakes, feverAnswers, feverTriggerCount, onGameOver]);

  const handlePointerDown = (x: number, y: number, e: React.PointerEvent) => {
    if (isTransitioning || timeLeft <= 0 || showHowTo || startSequence) return;
    const tile = grid[y][x];
    if (!isFever && tile.cooldownUntil > Date.now()) return;

    setIsDragging(true);
    setSelectedPath([{ x, y }]);
    setDragTotal(tile.value);
    pointerPosRef.current = { x: e.clientX, y: e.clientY };
    setBubblePos({ x: e.clientX, y: e.clientY });
  };

  // FIX 6: Throttle pointer move updates — only call setState for bubblePos
  // every ~16ms (one frame), not on every native pointer event.
  const lastPointerUpdateRef = useRef(0);

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    pointerPosRef.current = { x: e.clientX, y: e.clientY };

    const now = performance.now();
    if (now - lastPointerUpdateRef.current > 16) {
      lastPointerUpdateRef.current = now;
      setBubblePos({ x: e.clientX, y: e.clientY });
    }

    const element = document.elementFromPoint(e.clientX, e.clientY);
    if (!element) return;
    const tileEl = element.closest('[data-tile]');
    if (!tileEl) return;

    const x = parseInt(tileEl.getAttribute('data-x') || '-1');
    const y = parseInt(tileEl.getAttribute('data-y') || '-1');
    if (x === -1 || y === -1) return;

    const last = selectedPath[selectedPath.length - 1];
    if (last.x === x && last.y === y) return;

    const dx = Math.abs(x - last.x);
    const dy = Math.abs(y - last.y);
    if ((dx === 1 && dy === 0) || (dx === 0 && dy === 1)) {
      const existingIdx = selectedPath.findIndex(p => p.x === x && p.y === y);
      if (existingIdx !== -1) {
        const newPath = selectedPath.slice(0, existingIdx + 1);
        setSelectedPath(newPath);
        let newTotal = 0;
        newPath.forEach(p => newTotal += grid[p.y][p.x].value);
        setDragTotal(newTotal);
      } else {
        const tile = grid[y][x];
        if (!isFever && tile.cooldownUntil > Date.now()) return;

        const newPath = [...selectedPath, { x, y }];
        setSelectedPath(newPath);
        setDragTotal(prev => prev + tile.value);

        if (dragTotal + tile.value > currentTarget) {
          audioEngine.playWrong();
        }
      }
    }
  };

  const triggerFever = useCallback(() => {
    setIsTransitioning(true);
    setFeverTriggerCount(prev => prev + 1);
    audioEngine.playFeverStart('goldrush');

    setTimeout(() => {
      setIsFever(true);
      setFeverTimeLeft(10);
      setIsTransitioning(false);
    }, 800);
  }, []);

  useEffect(() => {
    if (tutorialStep === 15 || tutorialStep === 16 || tutorialStep === 17) {
      setCurrentTarget(10);
    } else if (tutorialStep === 18) {
      setCurrentTarget(11);
    } else if (tutorialStep === 19) {
      setCurrentTarget(12);
    }
  }, [tutorialStep]);

  useEffect(() => {
    if (tutorialStep === 20 && !isFever && !isTransitioning) {
      setIsFever(true);
      setFeverTimeLeft(10);
    }
  }, [tutorialStep, isFever, isTransitioning]);

  // FIX 4 applied: grid dependency removed from useCallback — use gridRef2.current instead.
  const handlePointerUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    const target = currentTarget;
    if (dragTotal === target) {
      audioEngine.playCorrect(0, 'goldrush', false, isFever);

      if (tutorialStep === 17) {
        setSelectedPath([]);
        setDragTotal(0);
        onNextTutorialStep?.();
        return;
      }
      if (tutorialStep === 18) {
        setSelectedPath([]);
        setDragTotal(0);
        onNextTutorialStep?.();
        return;
      }
      if (tutorialStep === 19) {
        setSelectedPath([]);
        setDragTotal(0);
        triggerFever();
        setTimeout(() => { onNextTutorialStep?.(); }, 800);
        return;
      }
      if (tutorialStep === 20 && isFever) {
        setSelectedPath([]);
        setDragTotal(0);
        setFeverCounter(prev => {
          const next = prev + 1;
          if (next >= 3) { onNextTutorialStep?.(); }
          return next;
        });
        return;
      }

      // Use gridRef2.current so 'grid' is NOT in the dep array.
      const currentGrid = gridRef2.current;
      const sparkingIds = selectedPath.map(p => currentGrid[p.y][p.x].id);

      setGrid(g => {
        if (!g || g.length === 0) return g;
        return g.map(row => row.map(tile =>
          sparkingIds.includes(tile.id) ? { ...tile, isSparking: true } : tile
        ));
      });

      setTimeout(() => {
        setGrid(g => {
          if (!g || g.length === 0) return g;
          return g.map(row => row.map(tile =>
            sparkingIds.includes(tile.id) ? { ...tile, isSparking: false } : tile
          ));
        });
      }, 400);

      if (isFever) {
        const points = Math.round(150 * (1 + (prismBuffs?.feverScoreMultiplier || 0)));
        setFeverAnswers(prev => prev + 1);
        setScore(prev => prev + points);
      } else {
        setCorrectAnswers(prev => prev + 1);
        setScore(prev => prev + 50);
        setFeverCounter(prev => {
          const next = prev + 1;
          if (next >= 5) {
            setFeverCounter(0);
            triggerFever();
            return 0;
          }
          return next;
        });
      }

      if (currentTarget < 60) setCurrentTarget(prev => prev + 1);

    } else {
      if (dragTotal > 0) {
        audioEngine.playWrong();
        setMistakes(prev => prev + 1);
      }
    }

    setSelectedPath([]);
    setDragTotal(0);
  }, [isDragging, dragTotal, currentTarget, isFever, feverCounter, triggerFever, tutorialStep, onNextTutorialStep, selectedPath, prismBuffs]);

  useEffect(() => {
    window.addEventListener('pointerup', handlePointerUp);
    return () => window.removeEventListener('pointerup', handlePointerUp);
  }, [handlePointerUp]);

  return (
    <div className={cn(
      'flex flex-col h-full select-none overflow-hidden transition-colors duration-1000 touch-none relative',
      isFever ? 'bg-[#1c1208]' : 'bg-slate-900'
    )}>
      {/* Vault Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="glass-sweep-overlay opacity-[0.03]" />
      </div>

      {/* FIX 7: Fever glow — replace the scale-animating motion.div (which causes
          full repaint every frame) with CSS-only opacity transition + a static
          radial gradient. No Framer Motion infinite loop on a full-screen div. */}
      <div
        className={cn(
          'absolute inset-0 pointer-events-none overflow-hidden z-0 transition-opacity duration-1000',
          isFever ? 'opacity-100' : 'opacity-0'
        )}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(251,191,36,0.3)_0%,rgba(0,0,0,0)_70%)] animate-pulse-slow" />
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/15 via-yellow-500/5 to-transparent mix-blend-screen" />
        <div className="absolute inset-0 bg-[radial-gradient(rgba(251,191,36,0.15)_1px,transparent_1px)] [background-size:24px_24px]" />
      </div>

      {/* Vault Header */}
      <div className="px-4 pt-6 pb-2 relative z-50">
        <div className={cn('glass-panel rounded-[40px] p-6 flex flex-col gap-4 shadow-2xl border-none relative overflow-hidden transition-colors', isFever && 'bg-slate-900/60')}>
          <div className="flex items-center justify-between">
            <button
              onClick={() => { isTutorialActive ? onBack() : setShowExitConfirm(true); }}
              className="w-12 h-12 flex items-center justify-center bg-white/5 rounded-2xl backdrop-blur-md border border-white/10 hover:bg-white/10 transition-all active:scale-95"
            >
              <ArrowLeft className="w-6 h-6 text-white stroke-[3]" />
            </button>
            <div className="flex flex-col items-center">
              <div className="text-[8px] font-black text-amber-500 uppercase tracking-[0.3em] leading-none mb-1">Best: {highScore.toLocaleString()}</div>
              <div className="text-[10px] font-black text-amber-500 uppercase tracking-[0.3em] leading-none mb-1">Vault Score</div>
              <div className="text-3xl font-num text-white leading-none">{score.toLocaleString()}</div>
            </div>
            <div className="flex items-center gap-2 bg-black/20 rounded-2xl px-4 py-2 border border-white/5">
              <span className="text-2xl font-num font-black text-white tabular-nums leading-none">{Math.ceil(isFever ? feverTimeLeft : timeLeft)}s</span>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="h-3 rounded-full bg-black/40 overflow-hidden relative shadow-inner p-0.5">
            <motion.div
              className={cn('h-full rounded-full relative', isFever ? 'bg-rose-500' : 'bg-gradient-to-r from-amber-600 via-yellow-400 to-amber-600')}
              initial={{ width: '100%' }}
              animate={{ width: `${((isFever ? feverTimeLeft : timeLeft) / (isFever ? 10 : (INITIAL_TIME + (prismBuffs?.extraTime || 0)))) * 100}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>
        </div>
      </div>

      {/* Target Display */}
      <div className={cn('mt-6 mb-2 flex flex-col items-center relative', isTutorialActive ? 'z-[10000]' : 'z-10')}>
        <div
          id="goldrush-tutorial-1"
          className={cn(
            'flex flex-col items-center bg-slate-950/50 backdrop-blur-md px-6 py-3.5 rounded-3xl border border-white/10 shadow-2xl transition-all duration-300 min-w-[125px]',
            isFever && 'border-amber-500/30 shadow-[0_0_20px_rgba(251,191,36,0.15)]'
          )}
        >
          <span className="text-[10px] font-black text-amber-500 tracking-[0.25em] uppercase mb-1.5 leading-none">TARGET</span>
          <span className="text-4xl font-num font-black text-white leading-none drop-shadow-[0_2px_10px_rgba(251,191,36,0.4)]">
            {currentTarget}
          </span>
        </div>
      </div>

      {/* Grid Area */}
      <div className="flex-1 flex items-center justify-center p-4 relative z-10">
        <div
          id="goldrush-fever-zone"
          ref={gridRef}
          className="grid gap-3 p-4 bg-white/5 backdrop-blur-md rounded-[32px] border border-white/10 shadow-2xl"
          style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}
          onPointerMove={handlePointerMove}
        >
          {grid.map((row, y) => row.map((tile, x) => {
            // FIX 5: O(1) set lookup instead of O(n) selectedPath.some()
            const isSelected = selectedSetRef.current.has(`${x},${y}`);
            const isLocked = !isFever && tile.cooldownUntil > Date.now();
            return (
              <div
                key={tile.id}
                id={`goldrush-tile-${x}-${y}`}
                data-tile
                data-x={x}
                data-y={y}
                onPointerDown={(e) => handlePointerDown(x, y, e)}
                className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center relative overflow-visible"
              >
                <BullionTile value={tile.value} isSelected={isSelected} isLocked={isLocked} isFever={isFever} />
                {tile.isSparking && <TileSparks />}
              </div>
            );
          }))}
        </div>
      </div>

      {/* Live Total Bubble */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            style={{ position: 'absolute', left: bubblePos.x, top: bubblePos.y - 80, zIndex: 1000, pointerEvents: 'none' }}
            className="flex flex-col items-center -translate-x-1/2"
          >
            <div className={cn('px-6 py-3 rounded-2xl shadow-2xl text-2xl font-black', dragTotal > currentTarget ? 'bg-rose-500 text-white' : 'bg-white text-slate-800')}>
              {dragTotal}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <GameModal isOpen={showExitConfirm} onClose={() => setShowExitConfirm(false)} onConfirm={onBack} type="confirm" title="QUIT GOLD RUSH?" message="Are you sure? Your current score will be lost." confirmLabel="QUIT" cancelLabel="STAY" />
      {showHowTo && <HowToPlay mode="goldrush" onClose={() => setShowHowTo(false)} />}

      {/* Start Sequence Overlay */}
      <AnimatePresence>
        {startSequence && !showHowTo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[100] flex items-center justify-center bg-slate-950/90 backdrop-blur-md"
          >
            <motion.div
              key={startSequence}
              initial={{ scale: 0.5, opacity: 0, rotate: -20 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              exit={{ scale: 2, opacity: 0 }}
              transition={{ type: 'spring', damping: 12 }}
              className="text-center"
            >
              <div className="text-8xl font-num text-cyan-400 drop-shadow-[0_0_20px_rgba(34,211,238,0.5)] mb-4">
                {startSequence === 'ready' ? 'READY?' : 'GO!'}
              </div>
              <div className="text-slate-400 font-black uppercase tracking-[0.3em] text-sm">
                {startSequence === 'ready' ? 'COLLECT THE GOLD!' : 'MATCH THE TARGET!'}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
